import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  fetchCardRedemptionCategories,
  fetchCardRedemptionCurrencies,
  fetchCardRedemptionTypes,
  pollCardRateEnquiry,
  submitCardRateEnquiry,
} from '../../services/cardRedemption';
import { fDateTime } from '../../utils/formatTime';
import { fNumber } from '../../utils/formatNumber';

const POLL_INTERVAL_MS = 2500;

export default function CardRateEnquiryDialog({ onClose, open }) {
  const [form, setForm] = useState({
    type: '',
    category: '',
    currency: '',
    total_amount: '',
    number_of_cards: '',
  });
  const [phase, setPhase] = useState('form');
  const [submitting, setSubmitting] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [error, setError] = useState('');
  const [enquiryId, setEnquiryId] = useState('');
  const [enquiryData, setEnquiryData] = useState(null);
  const [lookups, setLookups] = useState({
    categories: [],
    currencies: [],
    types: [],
  });
  const pollTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setForm({
        type: '',
        category: '',
        currency: '',
        total_amount: '',
        number_of_cards: '',
      });
      setPhase('form');
      setSubmitting(false);
      setLoadingLookups(false);
      setError('');
      setEnquiryId('');
      setEnquiryData(null);
      setLookups({ categories: [], currencies: [], types: [] });
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
      }
      return;
    }

    const loadLookups = async () => {
      try {
        setLoadingLookups(true);
        setError('');

        const [categoryResponse, currencyResponse, typeResponse] = await Promise.all([
          fetchCardRedemptionCategories(),
          fetchCardRedemptionCurrencies(),
          fetchCardRedemptionTypes(),
        ]);

        setLookups({
          categories: categoryResponse.data || [],
          currencies: currencyResponse.data || [],
          types: typeResponse.data || [],
        });
      } catch (requestError) {
        setError(requestError.message || 'Unable to load enquiry options');
      } finally {
        setLoadingLookups(false);
      }
    };

    loadLookups();
  }, [open]);

  const isValid = useMemo(
    () =>
      Boolean(
        form.type &&
          form.category &&
          form.currency &&
          Number(form.total_amount) > 0 &&
          Number(form.number_of_cards) > 0
      ),
    [form]
  );

  const pollEnquiry = async (id) => {
    try {
      const response = await pollCardRateEnquiry(id);
      const nextData = response.data || null;
      setEnquiryData(nextData);

      if (nextData?.status === 'pending') {
        pollTimerRef.current = window.setTimeout(() => pollEnquiry(id), POLL_INTERVAL_MS);
        return;
      }

      setPhase('result');
    } catch (requestError) {
      setError(requestError.message || 'Unable to fetch enquiry update');
      setPhase('result');
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');

      const selectedCategory = lookups.categories.find(
        (item) => String(item.category_id) === String(form.category)
      );

      if (!selectedCategory) {
        throw new Error('Please select a giftcard category');
      }

      const response = await submitCardRateEnquiry({
        type: form.type,
        category: JSON.stringify({
          category_id: selectedCategory.category_id,
          name: selectedCategory.name,
        }),
        currency: form.currency,
        total_amount: Number(form.total_amount),
        number_of_cards: Number(form.number_of_cards),
      });

      const nextEnquiryId = response.data?.enquiry_id || '';
      setEnquiryId(nextEnquiryId);
      setPhase('waiting');
      await pollEnquiry(nextEnquiryId);
    } catch (requestError) {
      setError(requestError.message || 'Unable to submit enquiry');
      setPhase('form');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : handleCancel} fullWidth maxWidth="sm">
      <DialogTitle>Rate enquiry</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          {phase === 'form' && (
            <>
              <Typography variant="body2" color="text.secondary">
                Select the giftcard category, currency, and type from server options, then enter amount and card count for the enquiry.
              </Typography>

              {loadingLookups ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <CircularProgress size={32} sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Loading giftcard enquiry options
                  </Typography>
                </Box>
              ) : (
                <>
                  <TextField
                    select
                    label="Category"
                    size="small"
                    value={form.category}
                    onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
                    fullWidth
                  >
                    {lookups.categories.map((category) => (
                      <MenuItem key={category.category_id} value={String(category.category_id)}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Currency"
                    size="small"
                    value={form.currency}
                    onChange={(event) => setForm((current) => ({ ...current, currency: event.target.value }))}
                    fullWidth
                  >
                    {lookups.currencies.map((currency) => (
                      <MenuItem key={currency.name} value={currency.name}>
                        {currency.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Type"
                    size="small"
                    value={form.type}
                    onChange={(event) => setForm((current) => ({ ...current, type: event.target.value }))}
                    fullWidth
                  >
                    {lookups.types.map((type) => (
                      <MenuItem key={type.type_id} value={type.name}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Total amount"
                      size="small"
                      type="number"
                      value={form.total_amount}
                      onChange={(event) => setForm((current) => ({ ...current, total_amount: event.target.value }))}
                      fullWidth
                    />

                    <TextField
                      label="Number of cards"
                      size="small"
                      type="number"
                      value={form.number_of_cards}
                      onChange={(event) => setForm((current) => ({ ...current, number_of_cards: event.target.value }))}
                      fullWidth
                    />
                  </Stack>
                </>
              )}
            </>
          )}

          {phase === 'waiting' && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress size={34} sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Waiting for rate lookup
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enquiry ID: {enquiryId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We&apos;re polling until the enquiry is no longer pending.
              </Typography>
            </Box>
          )}

          {phase === 'result' && enquiryData && (
            <>
              <Typography variant="subtitle1">Enquiry result</Typography>
              <Divider />
              <Stack spacing={1.25}>
                <Typography variant="body2">Enquiry ID: {enquiryData.enquiry_id}</Typography>
                <Typography variant="body2">Status: {enquiryData.status}</Typography>
                <Typography variant="body2">Category: {enquiryData.category}</Typography>
                <Typography variant="body2">Type: {enquiryData.type_name}</Typography>
                <Typography variant="body2">Currency: {enquiryData.country} ({enquiryData.currency})</Typography>
                <Typography variant="body2">Amount: {fNumber(enquiryData.amount)}</Typography>
                <Typography variant="body2">Cards: {enquiryData.number_of_cards}</Typography>
                <Typography variant="body2">Rate: {enquiryData.rate ?? 'Pending'}</Typography>
                <Typography variant="body2">Vendor rate: {enquiryData.vendor_rate ?? 'Pending'}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Settlement amount: {enquiryData.settlement_currency || ''}{' '}
                  {enquiryData.settlement_amount ? fNumber(enquiryData.settlement_amount) : 'Pending'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Updated at {fDateTime(enquiryData.updated_at)}
                </Typography>
                {enquiryData.hint && <Alert severity="info">{enquiryData.hint}</Alert>}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleCancel}>Cancel</Button>
        {phase === 'form' && (
          <LoadingButton
            loading={submitting}
            variant="contained"
            onClick={handleSubmit}
            disabled={!isValid || loadingLookups}
          >
            Submit enquiry
          </LoadingButton>
        )}
        {phase === 'result' && <Button variant="contained" onClick={handleCancel}>Done</Button>}
      </DialogActions>
    </Dialog>
  );
}

CardRateEnquiryDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};
